import { useMemo, useEffect } from "react";
import { ArrowLeft, Pause, Play, RotateCcw, SkipForward } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useRafNow } from "../hooks/useRafNow";
import { useTrainingEngine } from "../hooks/useTrainingEngine";
import { useTrainingAudio } from "../hooks/useTrainingAudio";
import { useStanceMotionAudio } from "../hooks/useStanceMotionAudio";
import { formatMMSS } from "../utils/formatTime";
import { TimerRing } from "./TimerRing";
import { StatusLabel } from "./StatusLabel";
import { SessionMeta } from "./SessionMeta";

type Props = {
    presetName: string;
    workSec: number;
    restSec: number;
    rounds: number;
    whistleStart: boolean;
    mode?: 'standard' | 'stance_motion' | 'whistle_starts';
    commands?: string[];
};

export function EliteTrainingEngine(props: Props) {
    const nav = useNavigate();
    const engine = useTrainingEngine({
        workSec: props.workSec,
        restSec: props.restSec,
        rounds: props.rounds,
        whistleStart: props.whistleStart,
    });

    const now = useRafNow(engine.state.isRunning);

    const remainingMs = useMemo(() => {
        if (!engine.state.phaseEndsAtMs) {
            // If we are ready, show full work time
            if (engine.state.phase === 'ready') return props.workSec * 1000;
            return 0;
        }
        // If paused, we technically need to store the remaining offset, 
        // but for this MVP architecture `now` updates stop when useRafNow(false).
        // So calculation stays valid relative to the last render? 
        // Actually, `useRafNow` stops updating `now`, so `phaseEndsAtMs - now` stays constant-ish 
        // IF `phaseEndsAtMs` was calculated relative to a `now` that matches. 
        // But `phaseEndsAtMs` is absolute wall time. 
        // If we pause, wall time continues. 
        // To properly pause with this absolute time model, we'd need to shift `phaseEndsAtMs` on resume.
        // The current `useTrainingEngine` `pause`/`resume` signature supports this (`resume(now, remaining)`).

        // However, for the *Display* while paused:
        // We simply use the `now` from state? 
        // If `useRafNow` stops, `now` is stale/frozen. 
        // `phaseEndsAtMs` - `frozenNow` = remaining. 
        // Correct!
        return Math.max(0, Math.round(engine.state.phaseEndsAtMs - now));
    }, [engine.state.phaseEndsAtMs, now, engine.state.phase, props.workSec]);

    const totalMs = useMemo(() => {
        if (engine.state.phase === "work") return props.workSec * 1000;
        if (engine.state.phase === "rest") return props.restSec * 1000;
        return props.workSec * 1000;
    }, [engine.state.phase, props.workSec, props.restSec]);

    const progress01 = totalMs === 0 ? 0 : remainingMs / totalMs;

    const timeLabel = formatMMSS(Math.ceil(remainingMs / 1000));

    // Audio Integration
    useTrainingAudio(engine.state.phase, engine.state.isRunning ? remainingMs : null);

    // Stance & Motion Audio
    useStanceMotionAudio(
        engine.state.phase,
        engine.state.isRunning,
        props.mode === 'stance_motion',
        props.commands
    );

    // Auto-transition when timer hits 0
    useEffect(() => {
        if (engine.state.isRunning && remainingMs <= 0 && engine.state.phase !== 'complete') {
            engine.next(performance.now());
        }
    }, [engine.state.isRunning, remainingMs, engine.next, engine.state.phase]);

    return (
        <div className="min-h-[100dvh] bg-[var(--bg)] text-[var(--text)] flex flex-col animate-fade-in relative z-[100] pb-safe">
            {/* Header */}
            <div className="h-14 px-4 flex items-center justify-between">
                <button
                    className="p-2 rounded-xl hover:bg-white/5 active:scale-95 transition"
                    onClick={() => nav(-1)}
                    aria-label="Back"
                >
                    <ArrowLeft className="w-5 h-5 text-[var(--muted)]" />
                </button>

                <div className="text-sm font-semibold tracking-wide text-[var(--text)]">
                    {props.presetName}
                </div>

                <div className="w-9" />
            </div>

            {/* Main */}
            <div className="flex-1 flex flex-col items-center justify-center px-4 gap-4">
                <StatusLabel phase={engine.state.phase} remainingMs={remainingMs} />

                <TimerRing
                    phase={engine.state.phase}
                    progress01={progress01}
                    timeLabel={timeLabel}
                />

                <SessionMeta
                    roundIndex={engine.state.roundIndex}
                    rounds={props.rounds}
                    workSec={props.workSec}
                    restSec={props.restSec}
                />

                {/* Controls */}
                <div className="mt-8 w-full max-w-sm flex items-center justify-between gap-4">
                    <IconButton
                        icon={<RotateCcw className="w-5 h-5" />}
                        label="Reset"
                        onClick={engine.reset}
                    />

                    {engine.state.isRunning ? (
                        <IconButton
                            icon={<Pause className="w-5 h-5" />}
                            label="Pause"
                            onClick={engine.pause}
                            primary
                        />
                    ) : (
                        // Resume logic: pass current 'now' and the calculated 'remainingMs'
                        <IconButton
                            icon={<Play className="w-5 h-5" />}
                            label={engine.state.phase === "ready" ? "Start" : "Resume"}
                            onClick={() => {
                                if (engine.state.phase === 'ready') {
                                    engine.start(performance.now());
                                } else {
                                    engine.resume(performance.now(), remainingMs);
                                }
                            }}
                            primary
                        />
                    )}

                    <IconButton
                        icon={<SkipForward className="w-5 h-5" />}
                        label="Next"
                        onClick={() => engine.next(performance.now())}
                    />
                </div>
            </div>

            {/* Safe exit (Optional - or we use the Back Arrow) */}
        </div>
    );
}

function IconButton(props: { icon: React.ReactNode; label: string; onClick: () => void; primary?: boolean }) {
    return (
        <button
            onClick={props.onClick}
            className={[
                "flex-1 h-14 rounded-2xl border transition active:scale-[0.98] outline-none",
                props.primary
                    ? "border-[var(--gold)]/40 bg-[var(--surface2)] shadow-[0_0_18px_rgba(201,162,39,0.12)]"
                    : "border-[var(--border)] bg-[var(--surface)] hover:bg-white/5",
            ].join(" ")}
        >
            <div className="h-full flex flex-col items-center justify-center gap-1">
                <div className={props.primary ? "text-[var(--gold)]" : "text-[var(--muted)]"}>{props.icon}</div>
                <div className="text-[11px] font-semibold tracking-wide text-[var(--muted)] uppercase">{props.label}</div>
            </div>
        </button>
    );
}
